// utils/apiUrls.js

/**
 * Utility to build full API URLs.
 * Only base URLs are read from process.env; all endpoint paths are hardcoded here.
 */

const trimTrailingSlash = (s) => (typeof s === 'string' ? s.replace(/\/+$/, '') : '');
const ensureLeadingSlash = (s) => (typeof s === 'string' ? (s.startsWith('/') ? s : '/' + s) : s);

/**
 * Read base URL from env and warn in dev if missing.
 */
function getBaseUrl(envName) {
  const raw = process.env[envName] || '';
  const trimmed = raw.trim();
  if (!trimmed && process.env.NODE_ENV !== 'production') {
    console.warn(`[apiUrls] Environment variable ${envName} is empty or missing.`);
  }
  return trimTrailingSlash(trimmed);
}

/**
 * Replace placeholders in path templates.
 * Supports both {key} and :key syntaxes.
 */
function interpolatePath(template, params = {}) {
  let result = template;
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const str = String(value);
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), str);
  });
  return result;
}

/**
 * Compose full URL from base env var and hardcoded path template.
 */
function buildUrl(baseEnvName, pathTemplate, pathParams) {
  const base = getBaseUrl(baseEnvName);
  let path = pathTemplate;
  if (pathParams) {
    path = interpolatePath(path, pathParams);
  }
  const normalizedPath = ensureLeadingSlash(path).replace(/\/{2,}/g, '/');
  return `${base}${normalizedPath}`;
}

/**
 * Hardcoded endpoint path templates (no path parts come from .env).
 */
const PATHS = {
  // OMS
  CREATE_ORDER: '/v2/oms/order',
  BULK_FORWARD_ORDER: '/v2/oms/bulk/order/create',
  SEARCH_ORDERS: '/v2/oms/search-orders',
  SHIPMENT_INVOICE: '/v2/oms/shipment-invoice',
  BULK_SHIPMENT_INVOICE: '/v2/oms/shipment-invoice/bulk',
  // OMS Internal – Merchant config (scheduled email report)
  MERCHANT_CONFIG_GET: '/v1/oms/internal/merchant-config/{merchantId}/MERCHANT_SCHEDULED_EMAIL_REPORT_CONFIGURATIONS',
  MERCHANT_CONFIG_PUT: '/v1/oms/internal/merchant-config',
  // OMS Internal – Merchant config (generic, parameterized by configuration key)
  MERCHANT_CONFIG_BY_KEY: '/v1/oms/internal/merchant-config/{merchantId}/{merchantConfigurationKey}',
  ORDER_STATUS_ANALYTICS: '/v2/oms/analytics/order-status',
  CHECK_SERVICEABILITY: '/v3/oms/check-merchant-serviceability',
  MERCHANT_DOMESTIC_PRICING_BATCH: '/v2/oms/merchant-domestic-pricing-batch',
  ADDRESS_LOOKUP_FROM_MOBILE: '/v2/oms/dropcustomerlookup',
  ALLOCATED_CARRIER_DETAILS: '/v2/oms/allocated-carrier-details',
  RETRIEVE_CARRIER_OPTIONS_IN_BATCH: '/v2/oms/carrier-options-batch',

  //NDR
  NDR_CALL_RECORDING_UPLOAD: '/v2/oms/ndr/call-recording/upload',
  NDR_CALL_RECORDING_LIST: '/v2/oms/ndr/call-recording/list',
  NDR_CALL_RECORDING_GET: '/v2/oms/ndr/call-recording/get',

  // POCAS
  GET_PICKUP_DETAILS: '/v1/pocas/onboarding/{parentClientId}/getpickupdetails',
  ADD_UPDATE_PICKUP: '/v1/pocas/onboarding/{merchantId}/pickup-details',
  DELETE_PICKUP: '/v1/pocas/onboarding/{merchantId}/pickup-details/{pickupAddressId}',
  SUBMIT_ONBOARDING: '/v1/pocas/onboarding/submit',
  GENERATE_LABEL: '/v1/pocas/label/download/',
  COD_REMITTANCE_SEARCH: '/v1/cod-remittance/reports',
  COD_REMITTANCE_REPORT: '/v1/cod-remittance/report',
  GET_MERCHANT_PROFILE: '/v1/pocas/admin/onboarding/{parentClientId}',
  CREATE_MANIFEST: '/api/v1/manifests/create',
  GET_MANIFEST_BY_ID: '/api/v1/manifests/{manifestId}',
  GET_MANIFEST_PRESIGNED_URL: '/api/v1/manifests/{manifestId}/presigned-url',
  UPLOAD_SIGNED_MANIFEST: '/api/v1/manifests/{manifestId}/upload-signed',
  SEARCH_MANIFEST: '/api/v1/manifests/search',
  CLAIMS_LIST: "/v1/pocas/claims", 
  CLAIM_CREATE: "/v1/pocas/claims",
  CLAIM_GET: "/v1/pocas/claims/{claimId}",
  CLAIM_UPDATE: "/v1/pocas/claims/{claimId}",
  CLAIM_DELETE: "/v1/pocas/claims/{claimId}",
  CLAIM_PROOFS: "/v1/pocas/claims/{claimId}/proofs",
  PROOFS_INIT: "/v1/pocas/proofs/init",
  PROOFS_COMPLETE: "/v1/pocas/proofs/{proofId}/completed",
  PROOFS_ATTACH: "/v1/pocas/proofs/{proofId}/attach-to-claim",
  UPLOADS_INIT: "/v1/pocas/uploads/init",
  UPLOADS_COMPLETE: "/v1/pocas/uploads/complete",
  UPLOADS_GET: "/v1/pocas/uploads/{uploadId}",
  CLAIM_MESSAGES: '/v1/pocas/claims/{claimId}/messages/list',
  POST_CLAIM_MESSAGE: '/v1/pocas/claims/{claimId}/messages',
  UPLOADS_ABORT: "/v1/pocas/uploads/{uploadId}/abort",
  CLAIM_REASONS: "/v1/pocas/claims/claim-reasons",
  UPLOAD_REFRESH_PRESIGNEDURL_BY_ID: "/v1/pocas/uploads/{uploadId}/download",

  // Wayubridge
  GET_PRODUCT_SKU: '/v1/wayubridge/productskumaster/merchant/{merchantId}/product-sku/{sku}',
  UPLOAD_PRODUCT_SKU_MASTER: '/v1/wayubridge/productskumaster/upload/{merchantId}',
  GET_MARKETPLACE_ORDERS: '/v1/wayubridge/marketplace/orders',
  UPDATE_MARKETPLACE_ORDERS: '/v1/wayubridge/marketplace/orders/update',
  FULLFILLMENT_MARKETPLACE_ORDER: '/v1/wayubridge/marketplace/fulfillment/create',
  CANCEL_MARKETPLACE_ORDER: '/v1/wayubridge/marketplace/orders/cancel',

  // IAM (Identity & Access Management)
  IAM_REGISTER: '/v1/iam/clients/register',
  IAM_AUTH: '/v1/iam/token/auth',

  // Onboarding
  ONBOARDING_INITIATE: '/v1/pocas/onboarding/initiate',
  ONBOARDING_UPDATE: '/v1/pocas/onboarding/update',
  ONBOARDING_UPLOAD_DOCUMENT: '/v1/pocas/onboarding/{merchantId}/upload/{documentType}',
  ONBOARDING_UPLOAD_LOGO: '/v1/pocas/onboarding/{merchantId}/upload-logo',
  ONBOARDING_SUBMIT: '/v1/pocas/onboarding/submit',

  // Wallet
  WALLET_ACTIVATE_PREPAID: '/v1/wallet/{merchantId}/activate/prepaid',

  // WayuTickets - Support Tickets
  TICKET_GET_BY_ORDER: '/v1/pocas/support/tickets/by-order',
  TICKET_CREATE: '/v1/wayutickets/tickets',
  TICKET_UPDATE: '/v1/wayutickets/tickets/{publicId}',
  TICKET_GET: '/v1/wayutickets/merchant/tickets/{publicId}',
  TICKET_LIST: '/v1/wayutickets/merchant/tickets',
  TICKET_CLOSE: '/v1/wayutickets/tickets/{publicId}/close',
  TICKET_REOPEN: '/v1/wayutickets/tickets/{publicId}/reopen',
  TICKET_ATTACHMENTS_UPLOAD: '/v1/wayutickets/tickets/{publicId}/attachments',
  TICKET_ATTACHMENT_DELETE: '/v1/wayutickets/tickets/{publicId}/attachments/{attachmentId}',
  TICKET_ATTACHMENT_DOWNLOAD: '/v1/wayutickets/tickets/{publicId}/attachments/{attachmentId}/download',
  TICKET_MESSAGES_LIST: '/v1/wayutickets/tickets/{publicId}/messages',
  TICKET_MESSAGE_ADD: '/v1/wayutickets/tickets/{publicId}/messages',
  TICKET_MESSAGE_UPDATE: '/v1/wayutickets/tickets/{publicId}/messages/{sequenceNumber}',
  TICKET_MESSAGE_DELETE: '/v1/wayutickets/tickets/{publicId}/messages/{sequenceNumber}',
  TICKET_MESSAGE_ATTACHMENT_ADD: '/v1/wayutickets/tickets/{publicId}/messages/{sequenceNumber}/attachments',
  TICKET_MESSAGE_ATTACHMENT_DELETE: '/v1/wayutickets/tickets/{publicId}/messages/{sequenceNumber}/attachments/{attachmentId}',
  TICKET_MESSAGE_ATTACHMENT_DOWNLOAD: '/v1/wayutickets/tickets/{publicId}/messages/{sequenceNumber}/attachments/{attachmentId}/download',
  TICKET_EVENTS_TIMELINE: '/v1/wayutickets/tickets/{publicId}/events/timeline',
  TICKET_DASHBOARD_SUMMARY: '/v1/wayutickets/merchant/tickets/ticketing-dashboard-summary',

  // POCAS - Merchant MIS Column Config (Report Configuration)
  MIS_COLUMN_CONFIG_ALLOWED: '/v1/pocas/mis-column-config/allowed',
  MIS_COLUMN_CONFIG: '/v1/pocas/mis-column-config',

  // OMS Dashboard – Merchant Operations Dashboard
  DASHBOARD_SUMMARY: '/v3/oms/dashboard/summary',
  DASHBOARD_SHIPMENTS_OVERVIEW: '/v3/oms/dashboard/shipments/overview',
  DASHBOARD_SHIPMENTS_STATUS: '/v3/oms/dashboard/shipments/status',
  DASHBOARD_SHIPMENTS_ZONE_WISE: '/v3/oms/dashboard/shipments/zone-wise',
  DASHBOARD_COURIER_SPLIT: '/v3/oms/dashboard/courier-split',
  DASHBOARD_DELIVERY_PERFORMANCE: '/v3/oms/dashboard/delivery-performance',
  DASHBOARD_REVENUE: '/v3/oms/dashboard/revenue',
  DASHBOARD_NDR: '/v3/oms/dashboard/ndr',
  DASHBOARD_COD: '/v3/oms/dashboard/cod',
  DASHBOARD_ATTEMPT_ANALYTICS: '/v3/oms/dashboard/attempt-analytics',

  // Draft Orders
  DRAFT_ORDER_CREATE: '/v2/oms/draft-orders',
  DRAFT_ORDER_GET: '/v2/oms/draft-orders/{draftOrderId}',
  DRAFT_ORDER_UPDATE: '/v2/oms/draft-orders/{draftOrderId}',
  DRAFT_ORDER_SEARCH: '/v2/oms/draft-orders/search',
  DRAFT_ORDER_DISCARD: '/v2/oms/draft-orders/{draftOrderId}',
  DRAFT_ORDER_PERMANENT_DELETE: '/v2/oms/draft-orders/{draftOrderId}/permanent',
  DRAFT_ORDER_BULK_DELETE: '/v2/oms/draft-orders/bulk-delete',
  DRAFT_ORDER_PLACE: '/v2/oms/draft-orders/{draftOrderId}/place',
  DRAFT_ORDER_BULK_PLACE: '/v2/oms/draft-orders/bulk-place',
  DRAFT_ORDER_DUPLICATE: '/v2/oms/draft-orders/{draftOrderId}/duplicate',
  DRAFT_ORDER_FROM_ORDER: '/v2/oms/draft-orders/from-order/{shipmentOrderId}',
  DRAFT_ORDER_BULK_UPLOAD: '/v2/oms/draft-orders/bulk-upload',

  // Order Confirmation
  SEARCH_CONFIRMATION_ORDERS: '/v2/oms/search-confirmation-orders',
  BYPASS_ORDER_CONFIRMATION: '/v2/oms/order/bypass-confirmation',

  // Unprocessed Orders
  SEARCH_UNPROCESSED_ORDERS: '/v2/oms/search-unprocessed-orders',
};

/**
 * Exported helpers.
 */
export const API = {
  // OMS
  createOrder: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.CREATE_ORDER),
  bulkForwardOrder: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.BULK_FORWARD_ORDER),
  searchOrders: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.SEARCH_ORDERS),
  shipmentInvoice: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.SHIPMENT_INVOICE),
  bulkShipmentInvoice: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.BULK_SHIPMENT_INVOICE),
  // OMS – Merchant scheduled email report config
  merchantConfigScheduledEmailGet: (merchantId) => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.MERCHANT_CONFIG_GET, { merchantId }),
  merchantConfigScheduledEmailPut: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.MERCHANT_CONFIG_PUT),
  // OMS – Merchant config (generic GET / PATCH for any of the 12 configuration keys)
  merchantConfigByKey: (merchantId, merchantConfigurationKey) =>
    buildUrl('REACT_APP_OMS_BASE_URL', PATHS.MERCHANT_CONFIG_BY_KEY, { merchantId, merchantConfigurationKey }),
  orderStatusAnalytics: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.ORDER_STATUS_ANALYTICS),
  checkServiceability: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.CHECK_SERVICEABILITY),
  merchantPricingBatch: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.MERCHANT_DOMESTIC_PRICING_BATCH),
  addressLookupFromMobile: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.ADDRESS_LOOKUP_FROM_MOBILE),
  allocatedCarrierDetails: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.ALLOCATED_CARRIER_DETAILS),
  retrieveCarrierOptionsInBatch: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.RETRIEVE_CARRIER_OPTIONS_IN_BATCH),

  // POCAS
  submitOnboarding: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.SUBMIT_ONBOARDING),
  generatePocasLabel: (orderRef) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.GENERATE_LABEL + orderRef),
  getCodRemittanceSearch: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.COD_REMITTANCE_SEARCH),
  getCodRemittanceReportUrl: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.COD_REMITTANCE_REPORT),
  getPickupDetails: (parentClientId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.GET_PICKUP_DETAILS, { parentClientId }),
  addOrUpdatePickupDetails: (merchantId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.ADD_UPDATE_PICKUP, { merchantId }),
  deletePickupDetails: (merchantId, pickupAddressId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.DELETE_PICKUP, { merchantId, pickupAddressId }),
  getMerchantProfile: (parentClientId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.GET_MERCHANT_PROFILE, { parentClientId }),
  createManifest: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.CREATE_MANIFEST),
  getManifestById: (manifestId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.GET_MANIFEST_BY_ID, { manifestId }),
  getManifestPresignedUrl: (manifestId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.GET_MANIFEST_PRESIGNED_URL, { manifestId }),
  uploadSignedManifest: (manifestId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.UPLOAD_SIGNED_MANIFEST, { manifestId }),
  searchManifest: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.SEARCH_MANIFEST),
  getClaimsList: () => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.CLAIMS_LIST),
  getClaim: (claimId) => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.CLAIM_GET, { claimId }),
  updateClaim: (claimId) => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.CLAIM_UPDATE, { claimId }),
  deleteClaim: (claimId) => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.CLAIM_DELETE, { claimId }),
  getClaimProofs: (claimId) => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.CLAIM_PROOFS, { claimId }),
  claimUpload: () => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.CLAIM_CREATE),
  proofsCompleted: (proofId) => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.PROOFS_COMPLETE, { proofId }),

  proofsInit: () => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.PROOFS_INIT),
  proofsComplete: (proofId) => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.PROOFS_COMPLETE, { proofId }),
  proofsAttach: (proofId) => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.PROOFS_ATTACH, { proofId }),

  uploadsInit: () => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.UPLOADS_INIT),
  uploadsComplete: () => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.UPLOADS_COMPLETE),
  getUpload: (uploadId) => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.UPLOADS_GET, { uploadId }),
  abortUpload: (uploadId) => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.UPLOADS_ABORT, { uploadId }),

  claimMessagesList: (claimId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.CLAIM_MESSAGES, { claimId }),
  postClaimMessage: (claimId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.POST_CLAIM_MESSAGE, { claimId }),
  getClaimReasons: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.CLAIM_REASONS),
  getUploadRefreshClaimPresignedUrlByUploadId: (uploadId, merchantId) => buildUrl("REACT_APP_POCAS_BASE_URL", PATHS.UPLOAD_REFRESH_PRESIGNEDURL_BY_ID, { uploadId }) + (merchantId ? `?merchantId=${encodeURIComponent(merchantId)}` : ""),


  // Wayubridge
  getProductSku: (merchantId, sku) => buildUrl('REACT_APP_WAYUBRIDGE_BASE_URL',PATHS.GET_PRODUCT_SKU,{ merchantId, sku }),
  uploadProductSkuMaster: (merchantId) => buildUrl('REACT_APP_WAYUBRIDGE_BASE_URL', PATHS.UPLOAD_PRODUCT_SKU_MASTER, { merchantId }),
  getMarketplaceOrder: () => buildUrl('REACT_APP_WAYUBRIDGE_BASE_URL', PATHS.GET_MARKETPLACE_ORDERS),
  updateMarketplaceOrders: () => buildUrl('REACT_APP_WAYUBRIDGE_BASE_URL', PATHS.UPDATE_MARKETPLACE_ORDERS),
  fullfillmentMarketplaceOrders: () => buildUrl('REACT_APP_WAYUBRIDGE_BASE_URL', PATHS.FULLFILLMENT_MARKETPLACE_ORDER),
  cancelMarketplaceOrder: () => buildUrl('REACT_APP_WAYUBRIDGE_BASE_URL', PATHS.CANCEL_MARKETPLACE_ORDER),

  // IAM
  iamRegister: () => buildUrl('REACT_APP_IAM_BASE_URL', PATHS.IAM_REGISTER),
  iamAuth: () => buildUrl('REACT_APP_IAM_BASE_URL', PATHS.IAM_AUTH),

  // Onboarding
  onboardingInitiate: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.ONBOARDING_INITIATE),
  onboardingUpdate: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.ONBOARDING_UPDATE),
  onboardingUploadDocument: (merchantId, documentType) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.ONBOARDING_UPLOAD_DOCUMENT, { merchantId, documentType }),
  onboardingUploadLogo: (merchantId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.ONBOARDING_UPLOAD_LOGO, { merchantId }),
  onboardingSubmit: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.ONBOARDING_SUBMIT),

  // Wallet
  walletActivatePrepaid: (merchantId) => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.WALLET_ACTIVATE_PREPAID, { merchantId }),

  // WayuTickets - Support Tickets
  getTicketByOrder: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_GET_BY_ORDER),
  createTicket: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_CREATE),
  updateTicket: (publicId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_UPDATE, { publicId }),
  getTicket: (publicId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_GET, { publicId }),
  listTickets: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_LIST),
  closeTicket: (publicId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_CLOSE, { publicId }),
  reopenTicket: (publicId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_REOPEN, { publicId }),
  uploadTicketAttachments: (publicId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_ATTACHMENTS_UPLOAD, { publicId }),
  deleteTicketAttachment: (publicId, attachmentId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_ATTACHMENT_DELETE, { publicId, attachmentId }),
  downloadTicketAttachment: (publicId, attachmentId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_ATTACHMENT_DOWNLOAD, { publicId, attachmentId }),
  listTicketMessages: (publicId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_MESSAGES_LIST, { publicId }),
  addTicketMessage: (publicId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_MESSAGE_ADD, { publicId }),
  updateTicketMessage: (publicId, sequenceNumber) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_MESSAGE_UPDATE, { publicId, sequenceNumber }),
  deleteTicketMessage: (publicId, sequenceNumber) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_MESSAGE_DELETE, { publicId, sequenceNumber }),
  addMessageAttachment: (publicId, sequenceNumber) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_MESSAGE_ATTACHMENT_ADD, { publicId, sequenceNumber }),
  deleteMessageAttachment: (publicId, sequenceNumber, attachmentId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_MESSAGE_ATTACHMENT_DELETE, { publicId, sequenceNumber, attachmentId }),
  downloadMessageAttachment: (publicId, sequenceNumber, attachmentId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_MESSAGE_ATTACHMENT_DOWNLOAD, { publicId, sequenceNumber, attachmentId }),
  getTicketTimeline: (publicId) => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_EVENTS_TIMELINE, { publicId }),
  getTicketDashboardSummary: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.TICKET_DASHBOARD_SUMMARY),

  // POCAS - Merchant MIS Column Config (Report Configuration)
  misColumnConfigAllowed: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.MIS_COLUMN_CONFIG_ALLOWED),
  misColumnConfigGet: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.MIS_COLUMN_CONFIG),
  misColumnConfigSave: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.MIS_COLUMN_CONFIG),
  misColumnConfigReset: () => buildUrl('REACT_APP_POCAS_BASE_URL', PATHS.MIS_COLUMN_CONFIG),

  uploadNDRCallRecording: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.NDR_CALL_RECORDING_UPLOAD),
  listNDRCallRecordings: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.NDR_CALL_RECORDING_LIST),
  getNDRCallRecording: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.NDR_CALL_RECORDING_GET),

  // OMS Dashboard – Merchant Operations Dashboard
  dashboardSummary: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DASHBOARD_SUMMARY),
  dashboardShipmentsOverview: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DASHBOARD_SHIPMENTS_OVERVIEW),
  dashboardShipmentsStatus: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DASHBOARD_SHIPMENTS_STATUS),
  dashboardShipmentsZoneWise: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DASHBOARD_SHIPMENTS_ZONE_WISE),
  dashboardCourierSplit: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DASHBOARD_COURIER_SPLIT),
  dashboardDeliveryPerformance: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DASHBOARD_DELIVERY_PERFORMANCE),
  dashboardRevenue: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DASHBOARD_REVENUE),
  dashboardNdr: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DASHBOARD_NDR),
  dashboardCod: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DASHBOARD_COD),
  dashboardAttemptAnalytics: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DASHBOARD_ATTEMPT_ANALYTICS),

  // Draft Orders
  createDraftOrder: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_CREATE),
  getDraftOrder: (draftOrderId) => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_GET, { draftOrderId }),
  updateDraftOrder: (draftOrderId) => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_UPDATE, { draftOrderId }),
  searchDraftOrders: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_SEARCH),
  discardDraftOrder: (draftOrderId) => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_DISCARD, { draftOrderId }),
  permanentDeleteDraftOrder: (draftOrderId) => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_PERMANENT_DELETE, { draftOrderId }),
  bulkDeleteDraftOrders: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_BULK_DELETE),
  placeDraftOrder: (draftOrderId) => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_PLACE, { draftOrderId }),
  bulkPlaceDraftOrders: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_BULK_PLACE),
  duplicateDraftOrder: (draftOrderId) => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_DUPLICATE, { draftOrderId }),
  draftOrderFromOrder: (shipmentOrderId) => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_FROM_ORDER, { shipmentOrderId }),
  bulkUploadDraftOrders: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.DRAFT_ORDER_BULK_UPLOAD),

  // Order Confirmation
  searchConfirmationOrders: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.SEARCH_CONFIRMATION_ORDERS),
  bypassOrderConfirmation: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.BYPASS_ORDER_CONFIRMATION),

  // Unprocessed Orders
  searchUnprocessedOrders: () => buildUrl('REACT_APP_OMS_BASE_URL', PATHS.SEARCH_UNPROCESSED_ORDERS),
};
